import React, { useCallback, useMemo, useState } from 'react';
import { Box, Chip, Stack, Typography, Divider, Tooltip, Button, IconButton, LinearProgress } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { orchestratorAgent } from '../services/orchestrator.js';
import { RunFinalView } from './components/RunFinalView.js';
import type { WidgetProps } from '../types/index.js';

type RunStatus = 'queued' | 'running' | 'complete' | 'needs_input' | 'error';

export const RunHeaderWidget: React.FC<WidgetProps> = ({ widget }) => {
  const inputs = Object.fromEntries((widget.inputs || []).map(i => [i.id, i.value]));
  const status: RunStatus = inputs.status || 'running';
  const summary: string | undefined = inputs.summary;
  const prompt: string | undefined = inputs.prompt;
  const finalText: string | undefined = inputs.final;
  const finalView: string | undefined = inputs.finalView;
  const finalData: any = inputs.finalData;
  const params: Record<string, any> | undefined = inputs.params;
  const trace: Array<{ t: number; text: string; tool?: string }> = inputs.trace || [];
  const nextSteps: string[] | undefined = inputs.nextSteps;
  const actionsTaken: string[] | undefined = inputs.actionsTaken;
  const cost: { estimatedUsd?: number } | undefined = inputs.cost;
  const [expanded, setExpanded] = useState<{ prompt: boolean; final: boolean }>({ prompt: true, final: true });

  const handleNextStepClick = useCallback((label: string) => {
    // Start a new Run directly from the CTA; do not block UI
    try {
      orchestratorAgent.executeTask(label).catch(() => {});
    } catch {}
  }, []);

  const statusColor = (
    status === 'running' ? 'primary' :
    status === 'queued' ? 'default' :
    status === 'complete' ? 'success' :
    status === 'needs_input' ? 'warning' :
    'error'
  ) as any;

  const latestStep = useMemo(() => (trace || []).slice(-1)[0], [trace]);

  return (
    <Box sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600} noWrap>
          {widget.config?.title || 'Run'}
        </Typography>
        <Tooltip title="Runs are scoped workspaces created for each prompt. They group the steps, widgets, and results so you can review, fork, and promote outcomes.">
          <IconButton size="small" sx={{ p: 0.5 }}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Chip size="small" color={statusColor} label={status} />
        {typeof cost?.estimatedUsd === 'number' && (
          <Chip size="small" variant="outlined" label={`$${cost!.estimatedUsd.toFixed(3)}`} />
        )}
      </Stack>

      {status === 'running' && (
        <LinearProgress color="primary" sx={{ height: 2, mb: 1 }} />
      )}

      {prompt && (
        <>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">Prompt</Typography>
            <Button size="small" onClick={() => setExpanded(s => ({ ...s, prompt: !s.prompt }))}>
              {expanded.prompt ? 'Collapse' : 'Expand'}
            </Button>
          </Stack>
          {expanded.prompt && (
            <Box sx={{
              mb: 1,
              p: 1,
              borderRadius: 1,
              bgcolor: 'action.hover',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}>
              <Typography variant="body2" color="text.primary">{prompt}</Typography>
            </Box>
          )}
        </>
      )}

      {summary && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {summary}
        </Typography>
      )}

      {Array.isArray(actionsTaken) && actionsTaken.length > 0 && (
        <Stack spacing={0.5} sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary">Actions</Typography>
          {actionsTaken.slice(0, 3).map((a, i) => (
            <Typography key={i} variant="caption" color="text.secondary">• {a}</Typography>
          ))}
        </Stack>
      )}

      {Array.isArray(nextSteps) && nextSteps.length > 0 && (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
          {nextSteps.slice(0, 3).map((s, i) => (
            <Button key={i} size="small" variant="outlined" onClick={() => handleNextStepClick(s)}>
              {s}
            </Button>
          ))}
        </Stack>
      )}

      {params && Object.keys(params).length > 0 && (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
          {Object.entries(params).slice(0, 6).map(([k, v]) => (
            <Chip key={k} size="small" variant="outlined" label={`${k}: ${String(v)}`} />
          ))}
        </Stack>
      )}

      <Divider sx={{ my: 1 }} />
      {(finalData || finalView) ? (
        <>
          <RunFinalView view={(finalView as any) || 'auto'} data={finalData} text={finalText} />
          <Divider sx={{ my: 1 }} />
        </>
      ) : (
        finalText && (
          <>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Final</Typography>
              <Tooltip title="Copy final response">
                <IconButton size="small" onClick={() => navigator.clipboard.writeText(finalText)}>
                  <ContentCopyIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
              <Button size="small" onClick={() => setExpanded(s => ({ ...s, final: !s.final }))}>
                {expanded.final ? 'Collapse' : 'Expand'}
              </Button>
            </Stack>
            <Box sx={{
              mb: 1,
              p: 1,
              borderRadius: 1,
              bgcolor: 'background.default',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: expanded.final ? 'none' : 160,
              overflow: 'auto',
            }}>
              <Typography variant="body2">{finalText}</Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
          </>
        )
      )}
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
        Steps {status === 'running' && latestStep ? `• ${latestStep.tool ? `${latestStep.tool}: ` : ''}${latestStep.text}` : ''}
      </Typography>
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {(trace as any[]).slice(-8).reverse().map((e, idx) => (
          <Tooltip key={idx} title={new Date(e.t).toLocaleTimeString()} placement="right">
            <Typography variant="caption" display="block" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {e.tool ? `${e.tool}: ` : ''}{e.text}
            </Typography>
          </Tooltip>
        ))}
        {(!trace || trace.length === 0) && (
          <Typography variant="caption" color="text.disabled">Awaiting steps…</Typography>
        )}
      </Box>
    </Box>
  );
};

export default RunHeaderWidget;
